import { InjectionToken } from '@angular/core'
import { environment } from '@awork/environments/environment'
import type { Environment } from '@awork/environments/types'
import getHostname from '@awork/_shared/functions/get-hostname'
import products from './products.json'

export type ProductId = 'awork' | 'aworkAI'
export type ProductCapability = 'productWelcome' | 'dashboard' | 'agentReports'

// Set by the local dev-server command; deployed builds select the product by host.
declare const LOCAL_PRODUCT_ID: ProductId | undefined

export interface ProductBranding {
  readonly displayName: string
  readonly websiteUrl: string
}

export interface ProductTracking {
  readonly segmentWriteKey: string
  readonly intercomAppId: string
  readonly linkedInSignedUpConversionIds: readonly number[]
  readonly linkedInOnboardingCompletedConversionIds: readonly number[]
}

export interface ProductConfiguration {
  readonly id: ProductId
  /** Default landing page, for example after login. */
  readonly homeRoute: string
  readonly capabilities: Readonly<Record<ProductCapability, boolean>>
  readonly branding: ProductBranding
  readonly tracking: ProductTracking
}

const definitions: Readonly<Record<ProductId, Omit<ProductConfiguration, 'id'>>> = products

/**
 * Selects a code-owned product without changing the deployment environment.
 * @param {string} hostname Browser hostname, without a port.
 * @param {ProductId} [localProduct] Build-time product selection; defaults to awork.
 * @returns {ProductConfiguration} Immutable product configuration.
 */
export function resolveProductConfiguration(
  hostname: string,
  localProduct: ProductId = typeof LOCAL_PRODUCT_ID === 'undefined' ? 'awork' : LOCAL_PRODUCT_ID
): ProductConfiguration {
  const isaworkAI =
    localProduct === 'aworkAI' ||
    ['bwork.io', 'awork-ai.com'].some(domain => hostname === domain || hostname.endsWith(`.${domain}`))

  return getProductConfiguration(isaworkAI ? 'aworkAI' : 'awork')
}

/**
 * Gets the configuration of one product.
 * @param {ProductId} id Product to read.
 * @returns {ProductConfiguration} Immutable product configuration.
 */
export function getProductConfiguration(id: ProductId): ProductConfiguration {
  const definition = definitions[id]

  return Object.freeze({
    id,
    homeRoute: definition.homeRoute,
    branding: Object.freeze({ ...definition.branding }),
    capabilities: Object.freeze({ ...definition.capabilities }),
    tracking: Object.freeze({ ...definition.tracking })
  })
}

/**
 * Gets the hosted workspace domain for a product.
 * @param {ProductId} product Product to open.
 * @param {Environment} productEnvironment Deployment environment.
 * @returns {string} Product workspace domain.
 */
export function resolveProductHostname(product: ProductId, productEnvironment: Environment = environment): string {
  const hostnames =
    productEnvironment === 'production'
      ? { awork: 'awork.com', aworkAI: 'awork-ai.com' }
      : { awork: 'cwork.io', aworkAI: 'bwork.io' }

  return hostnames[product]
}

/**
 * Selects the installed desktop protocol for the current product and environment.
 * @param product Product whose desktop app should open.
 * @param productEnvironment Current deployment environment.
 * @param hostname Hosted page that requested the handoff.
 * @returns The exact production, develop, local, or PR desktop protocol.
 */
export function resolveProductDesktopScheme(
  product: ProductId,
  productEnvironment: Environment = environment,
  hostname: string = window.location.hostname
): string {
  const runner = hostname.match(/^(?:[^.]+\.)?app-pr-(\d+)\.runner\.(?:cwork\.io|bwork\.io|awork\.com|awork-ai\.com)$/)

  if (runner) {
    return `${product.toLowerCase()}-preview-${runner[1]}`
  }

  return `${product.toLowerCase()}${productEnvironment === 'production' ? '' : `-${productEnvironment}`}`
}

/**
 * Builds a workspace URL in another product and keeps the current runner domain.
 * @param {ProductId} product Product to open.
 * @param {string} subdomain Workspace subdomain.
 * @param {string} baseHostname Current base domain, including the runner prefix.
 * @returns {string} Workspace URL without login tokens or query parameters.
 */
export function resolveProductWorkspaceUrl(
  product: ProductId,
  subdomain: string,
  baseHostname: string = getHostname()
): string {
  const host = baseHostname.match(/^(.*\.)?(cwork\.io|bwork\.io|awork\.com|awork-ai\.com)$/)
  const prefix = host?.[1] ?? ''
  let productEnvironment = environment

  if (host) {
    productEnvironment = host[2] === 'awork.com' || host[2] === 'awork-ai.com' ? 'production' : 'develop'
  }

  return `https://${subdomain}.${prefix}${resolveProductHostname(product, productEnvironment)}/`
}

export const PRODUCT_CONFIGURATION = new InjectionToken<ProductConfiguration>('PRODUCT_CONFIGURATION', {
  providedIn: 'root',
  factory: () => resolveProductConfiguration(window.location.hostname)
})
